"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TableRequest = exports.BinTableSlice = exports.BinTable = void 0;
exports.getBinaryTable = getBinaryTable;
const fetchHelp_1 = require("./fetchHelp");
function dataTypeSize(dtype) {
    switch (dtype) {
        case 'DOUBLE': return 8;
        case 'BYTE': return 1;
        case 'CHAR': return 2;
        case 'SHORT': return 2;
        default: return 4;
    }
}
function getColumnSizes(schema) {
    return schema.columns.map(dtype => dataTypeSize(dtype) * schema.size);
}
function getColumnOffsets(schema) {
    return getColumnSizes(schema).reduce((acc, colSize) => {
        let lastSize = acc[acc.length - 1];
        let newSize = lastSize + colSize;
        return acc.concat(newSize);
    }, [0]);
}
function dtypeToAccessor(dtype, view, check) {
    function wrap(inner) {
        return check ? (i => check(i) ? inner(i) : NaN) : inner;
    }
    switch (dtype) {
        case 'DOUBLE': return wrap(i => view.getFloat64(i * 8, false));
        case 'FLOAT': return wrap(i => view.getFloat32(i * 4, false));
        case 'INT': return wrap(i => {
            const rawInt = view.getInt32(i * 4, false);
            return rawInt == -2147483648 ? NaN : rawInt;
        });
        case 'BYTE': return wrap(i => view.getInt8(i));
        case 'SHORT': return wrap(i => view.getInt16(i * 2, false));
        case 'CHAR': return i => {
            const charCode = view.getUint16(i * 2, false);
            return (charCode == 0) ? "" : String.fromCharCode(charCode);
        };
        case 'STRING': throw new Error('String columns in BinTables are not supported at the moment.');
        default: throw new Error('Unsupported data type: ' + dtype);
    }
}
class Column {
    constructor(arrBuff, offset, length, dtype, check) {
        const valLength = dataTypeSize(dtype);
        const view = new DataView(arrBuff, offset, length * valLength);
        this.length = length;
        this.value = dtypeToAccessor(dtype, view, check);
    }
}
class BinTable {
    constructor(arrBuff, schema, flagGoodness) {
        this._length = schema.size;
        let columnOffsets = getColumnOffsets(schema);
        this._columns = schema.columns.map((dtype, idx) => {
            const flagIdx = schema.qflagLookup[idx];
            const valueGoodness = (flagGoodness === undefined || flagIdx === undefined)
                ? undefined
                : i => flagGoodness(this._columns[flagIdx].value(i).toString());
            return new Column(arrBuff, columnOffsets[idx], schema.size, dtype, valueGoodness);
        });
    }
    get nCols() {
        return this._columns.length;
    }
    get length() {
        return this._length;
    }
    column(i) {
        return this._columns[i];
    }
    row(i) {
        return this._columns.map(col => col.value(i));
    }
    subrow(i, columnIndices) {
        return columnIndices.map(colIdx => this._columns[colIdx].value(i));
    }
    value(row, column) {
        return this._columns[column].value(row);
    }
    chartValues(xCol, yCol) {
        return Array.from({ length: this._length }, (_, i) => {
            return { x: this.value(i, xCol), y: this.value(i, yCol) };
        });
    }
    values(columnIndices, converter) {
        return Array.from({ length: this._length }, (_, i) => {
            return converter(this.subrow(i, columnIndices));
        });
    }
    static get empty() {
        return new BinTable(new ArrayBuffer(0), { columns: [], qflagLookup: {}, size: 0 }, undefined);
    }
}
exports.BinTable = BinTable;
;
class BinTableSlice {
    constructor(offset, length) {
        this.offset = offset;
        this.length = length;
    }
}
exports.BinTableSlice = BinTableSlice;
class TableRequest {
    constructor(tableId, schema, columnNumbers, subFolder, slice) {
        this.tableId = tableId;
        this.schema = schema;
        this.columnNumbers = columnNumbers;
        this.subFolder = subFolder;
        this.slice = slice;
    }
    get returnedTableSchema() {
        return {
            columns: this.columnNumbers.map(i => this.schema.columns[i]),
            qflagLookup: this.columnNumbers.reduce((acc, colNum, idx, _) => {
                const flagNum = this.schema.qflagLookup[colNum];
                if (flagNum != undefined) {
                    const flagPos = this.columnNumbers.indexOf(flagNum);
                    if (flagPos >= 0)
                        acc[idx] = flagPos;
                }
                return acc;
            }, {}),
            size: this.slice?.length ?? this.schema.size
        };
    }
}
exports.TableRequest = TableRequest;
function getBinaryTable(tblRequest, url, flagGoodness) {
    return fetch(url || 'https://data.icos-cp.eu/cpb', {
        method: 'post',
        headers: {
            'Accept': 'application/octet-stream',
            'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(tblRequest)
    })
        .then(fetchHelp_1.checkStatus)
        .then(response => response.arrayBuffer())
        .then(response => {
        return new BinTable(response, tblRequest.returnedTableSchema, flagGoodness);
    });
}
